import {
  type FC,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useStore } from '@nanostores/react';
import classNames from 'classnames';

import { segmentColors } from '@iconik-io/shared-lib/src/react/components/shared/segment/constants';
import type {
  SegmentEntityBase,
  SegmentEntityWithIdNormalized,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import type { AssetsAssetVersionSchemaWithIdExtended } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { SHARE_AUTHORIZE_EMAIL } from '../../constants/modals';
import { SHARE_ROOT_ROUTE } from '../../constants/routes';
import { getBaseUrl } from '../../ducks/app/selectors';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  selectAsset,
  selectAssetOriginalFormatFirstMetadata,
  selectIsTimeBasedAsset,
} from '../../ducks/pages/asset/selectors';
import { selectCanEditSegment } from '../../ducks/segments/selectors';
import { SHARE_USER_EMAIL_PLACEHOLDER } from '../../ducks/share/constants';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import TextFieldWrapper from '../../ui/forms/field_wrappers/text/text';
import Label, { VARIANTS } from '../../ui/label/label';
import { ui } from '../../utils/helpers';
import DebugDumpEntity from '../debug_dump_entity/debug_dump_entity';
import {
  SEGMENT_TYPE_COMMENT,
  SEGMENT_TYPE_GENERIC,
  SEGMENT_TYPE_TAG,
} from '../segment_form/constants';
import { SegmentForm } from '../segment_form/segment_form';
import { SegmentValue } from '../segment_value/segment_value';
import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';
import SegmentControls from './segment_controls';
import { SegmentHeader } from './segment_header';
import { SegmentTimecode } from './segment_timecode';

import styles from './segment.module.css';

const allowedColorSegmentSet = new Set([
  SEGMENT_TYPE_COMMENT,
  SEGMENT_TYPE_GENERIC,
  SEGMENT_TYPE_TAG,
]);

type Props = {
  assetId: string;
  checked?: boolean;
  className: string;
  isCurrentVersion: boolean;
  onCheck?: (segmentId: string, name: string, value: boolean) => void;
  onDelete?: (segmentId: string) => void;
  onEdit: (
    originalSegment: SegmentEntityWithIdNormalized,
    values: Partial<SegmentEntityBase>,
  ) => Promise<unknown>;
  onEditMode?: (segmentId: string, metadataViewId?: string | null) => void;
  onReply?: (
    replyToSegmentId: string,
    segment: SegmentEntityBase,
    shareUserEmail?: string,
  ) => Promise<unknown>;
  segment: SegmentEntityWithIdNormalized;
  version?: AssetsAssetVersionSchemaWithIdExtended;
};

const Segment: FC<Props> = ({
  assetId,
  checked,
  className,
  isCurrentVersion,
  onCheck,
  onDelete,
  onEdit,
  onEditMode,
  onReply,
  segment,
  version,
}) => {
  const dispatch = useAppDispatch();
  const shareToken = useAppSelector((state) => state.share.shareAuthLogin);
  const shareLinkUserEmail = useAppSelector(
    (state) => state.share.shareLinkUserEmail,
  );
  const asset = useAppSelector((state) => selectAsset(state, assetId));
  const isAssetSubclip = asset?.type === 'SUBCLIP';

  const { route, params } = useLeafRouteParams();
  const versionId = params.versionId;
  const activeDrawingSegmentId = useAppSelector(
    (state) => state.assetCommentsReducer.activeDrawing?.segmentId,
  );
  const baseUrl = useAppSelector(getBaseUrl);
  const isTimeBasedAsset = useAppSelector((state) =>
    selectIsTimeBasedAsset(state, assetId, versionId),
  );
  const originalMetadata = useAppSelector((state) =>
    selectAssetOriginalFormatFirstMetadata(state, assetId, versionId),
  );
  const isPdf = originalMetadata?.format === 'PDF';

  const canEdit = useAppSelector((state) =>
    selectCanEditSegment(state, segment),
  );

  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const currentUnifiedSegment = useStore(
    unifiedPlayerStore.segment.$currentSegment,
  );
  const currentUnifiedSegmentId = currentUnifiedSegment.id;
  const [edit, setEdit] = useState(false);
  const [reply, setReply] = useState(false);
  const [activeSegment, setActiveSegment] = useState<
    SegmentEntityBase | undefined
  >(undefined);
  const [replySegment, setReplySegment] = useState<
    SegmentEntityBase | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  const replyButtonRef = useRef<HTMLButtonElement>(null);

  const editButtonRef = useRef<HTMLButtonElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: side effect
  useEffect(() => {
    // Metadata values for tags are not available at initial render. They are updated in parent
    // when entering edit mode. In this case we need to update activeSegment with correct values.
    // Otherwise, we will get empty tags
    if (activeSegment) {
      setActiveSegment({
        ...activeSegment,
        metadata: {
          ...activeSegment.metadata,
          _gcvi_tags: segment.metadata?._gcvi_tags ?? [],
        },
      });
    }
  }, [segment.metadata?._gcvi_tags]);

  const handleSegmentClick = useCallback(() => {
    if (!isCurrentVersion && !isAssetSubclip) {
      if (segment.time_start_milliseconds && segment.time_end_milliseconds) {
        unifiedPlayerStore.selectSegmentRange(
          {
            start: segment.time_start_milliseconds / 1000,
            end: segment.time_end_milliseconds / 1000,
          },
          { seek: true },
        );
      } else if (segment.time_start_milliseconds) {
        unifiedPlayerStore.setTargetTime(
          segment.time_start_milliseconds / 1000,
        );
      }
      return;
    }

    unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);
  }, [isCurrentVersion, isAssetSubclip, segment]);

  const stopClickPropagation = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleEdit = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      if (currentUnifiedSegmentId !== segment.id) {
        unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);
      }
      unifiedPlayerStore.segment.setCurrentSegmentEditing(true);
      setEdit(true);
      setReply(false);
      setActiveSegment(segment);
      onEditMode?.(segment.id, segment.metadata_view_id);
    },
    [segment, onEditMode, currentUnifiedSegmentId],
  );

  useEffect(() => {
    if (currentUnifiedSegmentId !== segment.id) {
      setEdit(false);
      setReply(false);
      unifiedPlayerStore.segment.annotation.setIsContextualAnnotationVisible(
        true,
      );
    }
  }, [currentUnifiedSegmentId, segment.id]);

  const segmentDelete = useCallback(() => {
    onDelete?.(segment.id);
  }, [onDelete, segment]);

  const needsEmailAuthorization = useCallback(() => {
    if (!route.startsWith(SHARE_ROOT_ROUTE)) {
      return false;
    }
    return shareToken?.email === SHARE_USER_EMAIL_PLACEHOLDER;
  }, [route, shareToken?.email]);

  const segmentReply = useCallback(() => {
    if (!onReply) {
      return;
    }

    if (!shareLinkUserEmail && needsEmailAuthorization()) {
      dispatch(openModal({ id: SHARE_AUTHORIZE_EMAIL }));
      return;
    }
    setReply(true);
    setEdit(false);
    setReplySegment({
      segment_type: segment.segment_type,
      asset_id: segment.asset_id,
      is_internal: segment.is_internal,
      version_id: segment.version_id,
    });
  }, [needsEmailAuthorization, onReply, segment, shareLinkUserEmail]);

  const handleChange = useCallback(
    (data: SegmentEntityBase) => {
      setActiveSegment({
        ...activeSegment,
        ...data,
      });
    },
    [activeSegment],
  );

  const handleEditSubmit = useCallback(
    async (data: SegmentEntityBase) => {
      if (!onEdit) {
        return;
      }

      setLoading(true);

      try {
        await onEdit(segment, data);
        setEdit(false);
        unifiedPlayerStore.segment.setCurrentSegmentEditing(false);
        unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
        setActiveSegment(undefined);
      } finally {
        setLoading(false);
      }
    },
    [onEdit, segment],
  );

  const handleReplyChange = useCallback(
    (data: SegmentEntityBase) => {
      setReplySegment({
        ...replySegment,
        ...data,
      });
    },
    [replySegment],
  );

  const handleReplySubmit = useCallback(
    (data: SegmentEntityBase) => {
      if (!onReply) {
        return Promise.resolve();
      }

      setLoading(true);

      return onReply(
        segment.id,
        {
          ...replySegment,
          ...data,
          is_internal: Boolean(segment.is_internal),
        },
        shareLinkUserEmail,
      )
        .then(() => {
          setReply(false);
          setReplySegment(undefined);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [onReply, segment, replySegment, shareLinkUserEmail],
  );

  const handleEditCancel = useCallback(() => {
    setEdit(false);
    unifiedPlayerStore.segment.setCurrentSegmentEditing(false);
    unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
  }, []);

  const handleReplyCancel = useCallback(() => {
    setReply(false);
  }, []);

  const handleApproveCommentClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      onCheck?.(segment.id, 'segment_checked', !checked);
    },
    [onCheck, segment, checked],
  );

  const showTimecode = !segment.parent_id && (isTimeBasedAsset || isPdf);
  const handleReply = onReply ? segmentReply : undefined;

  const wrapperClassName =
    isCurrentVersion || !version ? styles.wrapper : styles.wrapper_faded;

  if (segment.status === 'DELETED') {
    return (
      <div className={styles.deleted} role="listitem" aria-label="Segment item">
        Comment deleted
      </div>
    );
  }

  const colorObject = segmentColors.find(
    (item) => item.value === segment.segment_color,
  );

  const editBottomButtons = (
    <div className={styles.buttons}>
      {onDelete ? (
        <Button
          uiKey="comment__delete_button"
          iconName="deletion_delete"
          onClick={segmentDelete}
          wideBasis={false}
          noBorder
          variant="danger"
          disabled={loading}
          aria={{
            label: 'Delete comment',
          }}
        />
      ) : null}
      <div className={styles.buttons_right}>
        <Button
          variant="danger"
          onClick={handleEditCancel}
          className={styles.edit_button}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          ref={editButtonRef}
          variant="success"
          type="submit"
          className={styles.edit_button}
          disabled={loading}
        >
          Save
        </Button>
      </div>
    </div>
  );

  const replyBottomButtons = (
    <div className={styles.buttons}>
      <div className={styles.buttons_right}>
        <TextFieldWrapper
          className={styles.hidden}
          type="hidden"
          value={segment.id}
          name="parent_id"
        />
        <Button
          variant="danger"
          onClick={handleReplyCancel}
          className={styles.edit_button}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          ref={replyButtonRef}
          variant="success"
          type="submit"
          className={styles.edit_button}
          disabled={loading}
        >
          Reply
        </Button>
      </div>
    </div>
  );
  const isParentSegment = segment.asset_id !== assetId;

  return (
    <div
      className={classNames(wrapperClassName, className)}
      role="listitem"
      aria-label="Segment item"
      onClick={handleSegmentClick}
    >
      {segment.top_level ? (
        <div
          className={styles.color}
          aria-label={colorObject ? `Color ${colorObject.label}` : undefined}
        >
          <div style={{ background: segment.segment_color }} />
        </div>
      ) : null}
      <SegmentHeader
        onApproveClick={handleApproveCommentClick}
        segment={segment}
        onCheck={onCheck}
        onEdit={handleEdit}
        checked={checked}
        editButtonDisabled={isParentSegment}
        editButtonPrependElement={
          <TooltipTrigger
            tooltipText="Comment is made on a parent asset"
            component="div"
          >
            {isParentSegment ? (
              <Label
                className={styles.parent_comment_label}
                variant={VARIANTS.GRAY}
              >
                P
              </Label>
            ) : null}
          </TooltipTrigger>
        }
        canEdit={canEdit && !edit}
      />
      {edit && activeSegment ? (
        <div className={styles.content} onClick={stopClickPropagation}>
          <SegmentForm
            segment={activeSegment}
            className={styles.edit_form}
            withColorsPicker={
              allowedColorSegmentSet.has(segment.segment_type) &&
              segment.top_level
            }
            isColorPickerExpanded
            onSubmit={handleEditSubmit}
            onChange={handleChange}
            verticalControls
            bottomButtons={editBottomButtons}
            autoFocus
            editOrReplyMode
          />
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.comment}>
            <pre {...ui('segments-list-item-value')}>
              {showTimecode ? (
                <SegmentTimecode
                  assetId={assetId}
                  segment={segment}
                  inline={segment.segment_type === 'COMMENT'}
                />
              ) : null}
              <DebugDumpEntity entityKey="segments" id={segment.id} />
              <SegmentValue segment={segment} />
            </pre>
          </div>
          {reply && replySegment ? (
            <div onClick={stopClickPropagation}>
              <SegmentForm
                className={styles.edit_form}
                segment={replySegment}
                onSubmit={handleReplySubmit}
                onChange={handleReplyChange}
                verticalControls
                bottomButtons={replyBottomButtons}
                autoFocus
                editOrReplyMode
              />
            </div>
          ) : (
            <SegmentControls
              activeDrawingSegmentId={activeDrawingSegmentId}
              baseUrl={baseUrl}
              onReply={handleReply}
              segment={segment}
              replyButtonDisabled={isParentSegment}
              isCurrentVersion={isCurrentVersion}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Segment;
